import { getFindexSessionId } from "@/modules/auth/hooks/useSession"

const request = (input: RequestInfo | URL, init?: RequestInit) => {
  const headers = (init || {}).headers || {}
  return fetch(input, { ...(init || {}), headers })
}

export class BaseAPIClient {
  baseUrl: string
  queryString: string = ''
  headers: HeadersInit = {}
  apiKey?: string
  discardSession: boolean = false;

  constructor(baseUrl: string, queryString?: string, headers?: HeadersInit, apiKey?: string, discardSession?: boolean) {
    this.baseUrl = baseUrl
    this.queryString = queryString || ''
    this.headers = headers || {}
    this.apiKey = apiKey
    this.discardSession = !!discardSession;
  }

  setApiKey(key: string) {
    this.apiKey = key
  }

  urlFor(endpoint: string) {
    return (
      `${this.baseUrl}` +
      (endpoint.startsWith('/') || this.baseUrl.endsWith('/') ? endpoint : `/${endpoint}`) +
      `${this.queryString}`
    )
  }

  async getHeaders(): Promise<HeadersInit> {
    const sessionId = getFindexSessionId();
    return {
      ...this.headers,
      ...(this.apiKey
        ? {
            Authorization: 'Bearer ' + this.apiKey
          }
        : {}),
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(sessionId && !this.discardSession ? {
        'X-findexSessionId': sessionId,
        'access-control-expose-headers': 'X-findexSessionId'
      } : {})
    }
  }

  async get<T = any>(endpoint: string, extraHeaders: HeadersInit = {}) {
    const headers = await this.getHeaders()
    const resource = this.urlFor(endpoint)
    console.log({ resource })
    const resp = await request(resource, { headers: { ...headers, ...extraHeaders } })
    if (resp.status >= 300) {
      const text = await resp.text();
      throw new Error(text);
    }
    const data = await resp.json()
    return data as T
  }

  async delete<T = any>(endpoint: string, extraHeaders: HeadersInit = {}) {
    const headers = await this.getHeaders()
    const resource = this.urlFor(endpoint)
    const resp = await request(resource, {
      headers,
      ...extraHeaders,
      method: 'DELETE'
    })
    if (resp.status >= 300) {
      const text = await resp.text();
      throw new Error(text);
    }
    const data = await resp.json()
    return data as T
  }

  async patch<T = any, PayloadType = {}>(
    endpoint: string,
    payload: PayloadType,
    extraHeaders: HeadersInit = {}
  ) {
    const headers = await this.getHeaders()
    const resource = this.urlFor(endpoint)
    const resp = await request(resource, {
      headers,
      ...extraHeaders,
      method: 'PATCH',
      body: JSON.stringify(payload)
    })
    if (resp.status >= 300) {
      const text = await resp.text();
      throw new Error(text);
    }
    const data = await resp.json()
    return data as T
  }

  async post<T = any, PayloadType = {}>(
    endpoint: string,
    payload: PayloadType,
    callback?: (resp: Response) => void,
    extraHeaders: HeadersInit = {}
  ) {
    const headers = await this.getHeaders()
    const resource = this.urlFor(endpoint)
    const resp = await request(resource, {
      headers,
      ...extraHeaders,
      method: 'POST',
      body: JSON.stringify(payload)
    })
    if (callback) callback(resp);
    if (resp.status >= 300) {
      const text = await resp.text();
      throw new Error(text);
    }
    const data = await resp.json()
    return data as T
  }
}
